import {
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  CreateParams,
  CreateResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
  GetManyReferenceResult,
  GetManyReferenceParams,
} from "react-admin";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { schemas } from "../generated/serverClient";
import { z } from "zod";
import { getAuthenticatedServerClient } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";

type CustomCardDto = z.infer<typeof schemas.CustomCardDto>;
export type DrivingSchoolCustomCard = CustomCardDto;

type FileInputValue = File | { rawFile: File };
type CreateCustomCardDto = z.infer<typeof schemas.CreateCustomCardDto> & { image: FileInputValue };
type UpdateCustomCardDto = z.infer<typeof schemas.UpdateCustomCardDto> & { id: string; image?: FileInputValue };

class DrivingSchoolCustomCardsProvider extends AbstractDataProvider<DrivingSchoolCustomCard> {
  async getOne(
    _resource: string,
    { id }: GetOneParams<DrivingSchoolCustomCard>,
  ): Promise<GetOneResult<DrivingSchoolCustomCard>> {
    const serverClient = await getAuthenticatedServerClient();
    const customCard = await serverClient.getCustomCard({ params: { id } });
    return { data: customCard };
  }

  async getList(
    _resource: string,
    { filter, sort, pagination }: GetListParams,
  ): Promise<GetListResult<DrivingSchoolCustomCard>> {
    const { drivingSchoolId, ...clientSideFilter } = filter ?? {};
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const serverClient = await getAuthenticatedServerClient();
    const customCards = await serverClient.listCustomCards({ queries: { drivingSchoolId } });
    return applyPagination(applySort(applyFilter(customCards, clientSideFilter), sort), pagination);
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<DrivingSchoolCustomCard>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("customCards", { filter: { ...filter, drivingSchoolId: id }, sort, pagination });
  }

  async create(
    _resource: string,
    { data }: CreateParams<Partial<CreateCustomCardDto>>,
  ): Promise<CreateResult<DrivingSchoolCustomCard>> {
    const payload = schemas.CreateCustomCardDto.parse(data);
    const { ctaLabel, ctaLink, ...restPayload } = payload;
    const image = _toFile(data.image);
    if (!image) {
      throw new Error("image is required");
    }
    const normalizedCtaLabel = _normalizeOptionalText(ctaLabel);
    const normalizedCtaLink = _normalizeOptionalText(ctaLink);
    const serverClient = await getAuthenticatedServerClient();
    const customCard = await serverClient.createCustomCard({
      ...restPayload,
      ctaLabel: normalizedCtaLabel,
      ctaLink: normalizedCtaLink,
      image,
    });
    return { data: customCard };
  }

  async update(
    _resource: string,
    params: UpdateParams<UpdateCustomCardDto>,
  ): Promise<UpdateResult<DrivingSchoolCustomCard>> {
    const { id, data } = params;
    const serverClient = await getAuthenticatedServerClient();
    const payload = schemas.UpdateCustomCardDto.parse(data);
    const { ctaLabel, ctaLink, ...restPayload } = payload;
    const image = _toFile(data.image);
    const normalizedCtaLabel = _normalizeOptionalText(ctaLabel);
    const normalizedCtaLink = _normalizeOptionalText(ctaLink);
    const customCard = await serverClient.updateCustomCard(
      {
        ...restPayload,
        ...(image ? { image } : {}),
        ctaLabel: normalizedCtaLabel,
        ctaLink: normalizedCtaLink,
      },
      { params: { id } },
    );
    return { data: customCard };
  }

  async delete(_resource: string, { id }: DeleteParams<DrivingSchoolCustomCard>): Promise<DeleteResult> {
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteCustomCard(undefined, { params: { id } });
    return { data: {} };
  }
}

function _toFile(fileInputValue?: FileInputValue): File | undefined {
  if (!fileInputValue) {
    return undefined;
  }
  if (fileInputValue instanceof File) {
    return fileInputValue;
  }
  return fileInputValue.rawFile;
}

function _normalizeOptionalText(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

export const drivingSchoolCustomCardsProvider = new DrivingSchoolCustomCardsProvider();
